/**
 * Static Bond Rendering from Recipe
 *
 * Renders bonds using precalculated recipes and static positions.
 * Used by SVGCanvas for non-interactive rendering.
 */

import type { JSX } from 'react';
import { computeBondEndPoints, computeLineAngleBondEndPoints } from '@alchemiesolns/bond-calc-pure';
import type { FormatOptions } from '@alchemiesolns/svg-format';
import { BondRecipe } from './types/bondRecipe.types';

export interface BondVisualStyle {
  stroke?: string;
  strokeWidth?: number;
  opacity?: number;
}

/**
 * Renders a bond from a recipe and static atom positions
 *
 * @param recipe - Precalculated bond recipe with offsets and metadata
 * @param atom1X - X position of first atom (SVG coordinates)
 * @param atom1Y - Y position of first atom (SVG coordinates)
 * @param atom2X - X position of second atom (SVG coordinates)
 * @param atom2Y - Y position of second atom (SVG coordinates)
 * @param format - Format object from useFormat() hook
 */
export function renderBondFromRecipe(
  recipe: BondRecipe,
  atom1X: number,
  atom1Y: number,
  atom2X: number,
  atom2Y: number,
  format: FormatOptions
): JSX.Element[] {
  const a1Pos = [atom1X, atom1Y, 0];
  const a2Pos = [atom2X, atom2Y, 0];

  const elements: JSX.Element[] = [];

  // Render each tube/line
  recipe.tubes.forEach((tube) => {
    // Calculate endpoints with perpendicular offset
    const endPoints = computeBondEndPoints(a1Pos, a2Pos, [0, 0, 1], tube.offset);

    // Apply endpoint shortening based on element symbols and neighbor counts
    const finalEndPoints = computeLineAngleBondEndPoints(
      recipe.element1,
      recipe.element2,
      endPoints.a1,
      endPoints.a2,
      recipe.atom1NeighborCount,
      recipe.atom2NeighborCount,
      recipe.order,
      tube.lengthFactor,
      tube.lineIndex,
      tube.centered,
      format,
      recipe.atom1VisibilityFlag,
      recipe.atom2VisibilityFlag
    );

    // Special bond rendering (wedge/dash/squiggle)
    if (recipe.special && tube.lineIndex === 1) {
      const { special } = recipe;
      const midX = (endPoints.a1[0] + endPoints.a2[0]) / 2;
      const midY = (endPoints.a1[1] + endPoints.a2[1]) / 2;

      const vectorX = endPoints.a2[0] - endPoints.a1[0];
      const vectorY = endPoints.a2[1] - endPoints.a1[1];
      const angle = (Math.atan2(vectorY, vectorX) * 180) / Math.PI;

      elements.push(
        <g key={`${recipe.bondId}-${tube.lineIndex}-wedge`}>
          {/* Invisible hitbox line */}
          <line
            key={`${recipe.bondId}-${tube.lineIndex}`}
            id={recipe.bondId}
            className="svg-bond"
            data-bond-id={recipe.bondId}
            data-bond-index={String(recipe.bondIndex)}
            data-atom1-id={recipe.atom1Id}
            data-atom2-id={recipe.atom2Id}
            data-atom1-index={String(recipe.atom1Index)}
            data-atom2-index={String(recipe.atom2Index)}
            data-order={String(recipe.order)}
            data-line-index={String(tube.lineIndex)}
            data-visibility={recipe.visibility}
            x1={endPoints.a1[0]}
            y1={endPoints.a1[1]}
            x2={endPoints.a2[0]}
            y2={endPoints.a2[1]}
            stroke="transparent"
            strokeWidth={format.bond.thickness}
            opacity={0}
          />

          {/* Visual wedge/dash graphics */}
          <g
            transform={`translate(${midX - special.boxX}, ${midY - special.boxY})`}
            style={{ pointerEvents: 'none' }}
          >
            <g transform={`rotate(${angle - 90}, ${special.boxX}, ${special.boxY})`}>
              <use href={`#${special.type}${special.atomType}`} />
            </g>
          </g>
        </g>
      );
    } else {
      // Regular line bond
      elements.push(
        <line
          key={`${recipe.bondId}-${tube.lineIndex}`}
          id={recipe.bondId}
          className="svg-bond"
          data-bond-id={recipe.bondId}
          data-bond-index={String(recipe.bondIndex)}
          data-atom1-id={recipe.atom1Id}
          data-atom2-id={recipe.atom2Id}
          data-atom1-index={String(recipe.atom1Index)}
          data-atom2-index={String(recipe.atom2Index)}
          data-order={String(recipe.order)}
          data-line-index={String(tube.lineIndex)}
          data-visibility={recipe.visibility}
          x1={finalEndPoints.a1[0]}
          y1={finalEndPoints.a1[1]}
          x2={finalEndPoints.a2[0]}
          y2={finalEndPoints.a2[1]}
          stroke="black"
          strokeWidth={format.bond.thickness}
        //   strokeLinecap="round"
        //   strokeLinejoin="round"
        />
      );
    }
  });

  return elements;
}
